import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const CountUp = ({
  end,
  suffix = '',
  duration = 2.5,
  inView,
}: {
  end: number;
  suffix?: string;
  duration?: number;
  inView: boolean;
}) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;
    const startTime = Date.now();
    const isDecimal = end % 1 !== 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;
      setCount(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, end, duration]);

  return (
    <span>
      {end % 1 !== 0 ? count.toFixed(1) : count.toLocaleString('tr-TR')}
      {suffix}
    </span>
  );
};

const SustainabilityPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const bgX = useTransform(mouseX, [0, 1], [-8, 8]);
  const bgY = useTransform(mouseY, [0, 1], [-8, 8]);

  const [refPillars, inViewPillars] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refProcess, inViewProcess] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refGoals, inViewGoals] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refCommitment, inViewCommitment] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refStats, inViewStats] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refCTA, inViewCTA] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouseX.set((e.clientX - rect.left) / rect.width);
      mouseY.set((e.clientY - rect.top) / rect.height);
    },
    [mouseX, mouseY]
  );

  const heroStats = [
    { number: 100, suffix: '%', label: 'Geri Dönüşüm', icon: 'ri-recycle-line' },
    { number: 30, suffix: '%', label: 'Enerji Tasarrufu', icon: 'ri-flashlight-line' },
    { number: 2027, suffix: '', label: 'Karbon Nötr Hedef', icon: 'ri-leaf-line' },
  ];

  const sustainabilityPillars = [
    {
      icon: 'ri-recycle-line',
      title: 'Geri Dönüştürülebilir Ambalaj',
      description: 'Tüm ürünlerimiz %100 geri dönüştürülebilir PET ve PP malzemeden üretilmektedir.',
      color: 'from-green to-teal',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Çevreci Üretim',
      description: 'Enerji verimliliği yüksek makineler ve temiz üretim teknolojileri kullanıyoruz.',
      color: 'from-teal to-green',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Su ve Enerji Tasarrufu',
      description: 'Kapalı devre soğutma sistemleri ile kaynak tüketimini minimize ediyoruz.',
      color: 'from-green to-teal',
    },
    {
      icon: 'ri-plant-line',
      title: 'Karbon Ayak İzi Azaltma',
      description: 'Yerel tedarik ve yenilenebilir enerji kullanımı ile emisyonu düşürüyoruz.',
      color: 'from-teal to-green',
    },
  ];

  const circularProcess = [
    {
      number: '01',
      title: 'Hammadde Seçimi',
      desc: 'Virgin ve geri dönüştürülmüş PET kullanımı',
      icon: 'ri-seedling-line',
    },
    {
      number: '02',
      title: 'Verimli Üretim',
      desc: 'Enerji tasarruflu makinelerle üretim',
      icon: 'ri-settings-3-line',
    },
    {
      number: '03',
      title: 'Hafif Tasarım',
      desc: 'Optimize edilmiş malzeme kullanımı',
      icon: 'ri-scales-3-line',
    },
    {
      number: '04',
      title: 'Kullanım',
      desc: 'Dayanıklı ve güvenli ambalaj',
      icon: 'ri-hand-heart-line',
    },
    {
      number: '05',
      title: 'Toplama',
      desc: 'Geri dönüşüm için ayrıştırma',
      icon: 'ri-inbox-archive-line',
    },
    {
      number: '06',
      title: 'Geri Dönüşüm',
      desc: 'Yeni hammaddeye dönüştürme',
      icon: 'ri-loop-right-line',
    },
  ];

  const environmentalGoals = [
    {
      year: '2024',
      goal: 'Üretimde %30 Enerji Tasarrufu',
      status: 'Devam Ediyor',
      icon: 'ri-flashlight-line',
      progress: 75,
    },
    {
      year: '2025',
      goal: 'Sıfır Atık Hedefi',
      status: 'Planlı',
      icon: 'ri-delete-bin-line',
      progress: 40,
    },
    {
      year: '2026',
      goal: '%50 Yenilenebilir Enerji',
      status: 'Planlı',
      icon: 'ri-sun-line',
      progress: 25,
    },
    {
      year: '2027',
      goal: 'Karbon Nötr Üretim',
      status: 'Hedef',
      icon: 'ri-leaf-line',
      progress: 10,
    },
  ];

  const impactStats = [
    { value: 500, suffix: '+', label: 'Ton Geri Dönüştürülen PET', icon: 'ri-recycle-line' },
    { value: 40, suffix: '%', label: 'Su Tasarrufu', icon: 'ri-drop-line' },
    { value: 1200, suffix: '+', label: 'Ağaç Eşdeğeri Koruma', icon: 'ri-plant-line' },
    { value: 85, suffix: '%', label: 'Atık Geri Kazanımı', icon: 'ri-refresh-line' },
  ];

  const commitments = [
    {
      title: 'Virgin ve Geri Dönüştürülmüş Hammadde',
      desc: 'Hem virgin PET hem de geri dönüştürülmüş PET kullanarak çevre dostu üretim yapıyoruz',
    },
    {
      title: 'Atık Minimizasyonu',
      desc: 'Üretim sürecinde oluşan fire ve atıkları geri kazanım sistemleriyle değerlendiriyoruz',
    },
    {
      title: 'Hafif Ambalaj Tasarımı',
      desc: 'Daha az malzeme kullanarak aynı dayanıklılığı sağlayan optimize tasarımlar geliştiriyoruz',
    },
    {
      title: 'Çalışan Bilinçlendirme',
      desc: 'Tüm ekibimize düzenli çevre ve sürdürülebilirlik eğitimleri veriyoruz',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-poppins">
      <Navbar />

      {/* Hero Section */}
      <section
        ref={containerRef}
        onMouseMove={handleMouseMove}
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background Image with Parallax */}
        <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-[-20px]">
          <video
            src="https://public.readdy.ai/ai/video_res/90a2aace-10b4-4285-aac3-e57c689af911.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover object-center scale-105"
          />
        </motion.div>

        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/30" />

        {/* Soft Glowing Orbs */}
        <motion.div
          animate={{
            x: [0, 15, -10, 0],
            y: [0, -10, 15, 0],
            scale: [1, 1.08, 0.95, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[15%] right-[20%] w-72 h-72 bg-green/10 rounded-full blur-[120px] z-[1]"
        />
        <motion.div
          animate={{
            x: [0, -20, 10, 0],
            y: [0, 15, -10, 0],
            scale: [1, 0.92, 1.05, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-[20%] left-[10%] w-96 h-96 bg-teal/8 rounded-full blur-[140px] z-[1]"
        />

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-12 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[calc(100vh-160px)]">
            {/* Left Content */}
            <div className="lg:col-span-7 xl:col-span-7">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isLoaded ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                className="mb-6 sm:mb-8"
              >
                <span className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/10 border border-white/20 rounded-full backdrop-blur-md">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green" />
                  </span>
                  <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">
                    %100 Geri Dönüştürülebilir Ürünler
                  </span>
                </span>
              </motion.div>

              {/* Title */}
              <div className="mb-6 sm:mb-8 overflow-hidden">
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-2"
                >
                  Geleceğe Sorumlu,
                </motion.h1>
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold leading-[1.1]"
                >
                  <span className="bg-gradient-to-r from-green via-teal to-green bg-clip-text text-transparent">
                    Doğaya Saygılı
                  </span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
                className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 max-w-xl leading-relaxed"
              >
                Çevre dostu üretim süreçleri ve geri dönüştürülebilir ambalaj çözümleriyle 
                döngüsel ekonomi modelini benimseyerek sürdürülebilir bir gelecek inşa ediyoruz.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
                className="flex flex-col sm:flex-row items-start gap-4 mb-12 sm:mb-16"
              >
                <a
                  href="#hedefler"
                  className="group relative px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-green to-teal text-white text-sm sm:text-base font-semibold rounded-lg overflow-hidden transition-all duration-500 shadow-lg shadow-green/25 hover:shadow-xl hover:shadow-green/30 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Çevresel Hedeflerimiz
                    <motion.i
                      animate={{ y: [0, 3, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      className="ri-arrow-down-line text-lg"
                    />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-teal to-green opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </a>
                <a
                  href="#dongusel"
                  className="group px-8 sm:px-10 py-3.5 sm:py-4 bg-white/5 border border-white/30 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-white/15 hover:border-white/50 hover:-translate-y-0.5 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-recycle-line text-green" />
                    Döngüsel Ekonomi
                  </span>
                </a>
              </motion.div>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
                className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8"
              >
                {heroStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1, ease: 'easeOut' }}
                    className="group flex items-center gap-3 sm:gap-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-5 sm:px-6 py-3.5 sm:py-4 hover:bg-white/10 hover:border-green/30 transition-all duration-500 cursor-default"
                  >
                    <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-green/20 rounded-lg group-hover:bg-green/30 transition-colors duration-500">
                      <i className={`${stat.icon} text-green text-lg sm:text-xl`} />
                    </div>
                    <div>
                      <div className="text-xl sm:text-2xl font-bold text-white">
                        <CountUp end={stat.number} suffix={stat.suffix} inView={isLoaded} />
                      </div>
                      <div className="text-white/60 text-xs sm:text-sm font-medium">{stat.label}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right Side - Floating Cards */}
            <div className="hidden lg:flex lg:col-span-5 xl:col-span-5 relative items-center justify-center">
              <div className="relative w-full h-[500px]">
                {/* Central Glow */}
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.25, 0.4, 0.25],
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-green/15 rounded-full blur-[100px]"
                />

                {/* Subtle Ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/5 rounded-full"
                />

                {/* Floating Feature Cards */}
                {[
                  { name: 'Geri Dönüşüm', icon: 'ri-recycle-line', x: 'right-[8%]', y: 'top-[18%]', delay: 0.3 },
                  { name: 'Enerji Tasarrufu', icon: 'ri-flashlight-line', x: 'right-[3%]', y: 'top-[45%]', delay: 0.5 },
                  { name: 'Sıfır Atık', icon: 'ri-leaf-line', x: 'right-[12%]', y: 'top-[72%]', delay: 0.7 },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={isLoaded ? { opacity: 1, scale: 1, y: [0, -8, 0] } : {}}
                    transition={{
                      opacity: { duration: 0.8, delay: item.delay, ease: 'easeOut' },
                      scale: { duration: 0.8, delay: item.delay, ease: 'easeOut' },
                      y: { duration: 5 + index, repeat: Infinity, ease: 'easeInOut' },
                    }}
                    className={`absolute ${item.x} ${item.y}`}
                  >
                    <div className="group bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 sm:p-6 hover:bg-white/15 hover:border-green/40 transition-all duration-500 cursor-pointer shadow-xl shadow-black/10 min-w-[160px]">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-green/30 to-teal/30 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-500">
                        <i className={`${item.icon} text-green text-2xl`} />
                      </div>
                      <div className="text-white font-semibold text-sm">{item.name}</div>
                      <div className="flex items-center gap-1 mt-2 text-green/80 text-xs font-medium">
                        <span>Detaylar</span>
                        <i className="ri-arrow-right-s-line text-sm" />
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Center Icon */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 1, delay: 0.8, ease: 'easeOut' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl shadow-2xl shadow-green/30"
                  >
                    <i className="ri-earth-line text-white text-4xl" />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.5 }}
          className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        >
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-1.5"
          >
            <motion.div
              animate={{ y: [0, 10, 0], opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1.5 h-1.5 bg-green rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Sustainability Pillars */}
      <section ref={refPillars} className="py-20 lg:py-28 bg-white relative overflow-hidden">
        {/* Background Animation */}
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-10 w-96 h-96 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-teal/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewPillars ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, letterSpacing: '0.05em' }}
              animate={inViewPillars ? { opacity: 1, letterSpacing: '0.2em' } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-teal text-xs sm:text-sm font-semibold uppercase inline-block"
            >
              /SÜRDÜRÜLEBİLİRLİK İLKELERİ
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Çevre Dostu Yaklaşımımız
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Doğaya saygılı üretim anlayışımızın temel taşları
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {sustainabilityPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateX: -15 }}
                animate={inViewPillars ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{
                  duration: 0.7,
                  delay: index * 0.12,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="group relative"
              >
                <div className="relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-green/30 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 cursor-default overflow-hidden h-full">
                  {/* Top Gradient Line */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${pillar.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                      className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-green/5 to-transparent skew-x-12"
                    />
                  </div>

                  <div className="relative">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 8 }}
                      transition={{ duration: 0.4 }}
                      className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${pillar.color} rounded-xl text-white mb-6 shadow-lg`}
                    >
                      <i className={`${pillar.icon} text-2xl`} />
                    </motion.div>

                    <h3 className="text-xl font-bold text-navy mb-3 group-hover:text-green transition-colors duration-300">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{pillar.description}</p>
                  </div>

                  {/* Bottom Accent */}
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${pillar.color} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Circular Economy Process */}
      <section
        ref={refProcess}
        id="dongusel"
        className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      >
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 15, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /DÖNGÜSEL EKONOMİ
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Sürdürülebilir Döngü
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Hammaddeden geri dönüşüme, kapalı döngü üretim modelimiz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {circularProcess.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={inViewProcess ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="group relative"
              >
                <div className="relative bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 hover:border-green/30 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default overflow-hidden">
                  {/* Step Number Background */}
                  <div className="absolute top-4 right-4 text-6xl sm:text-7xl font-bold text-gray-50 group-hover:text-green/8 transition-colors duration-500 select-none leading-none">
                    {item.number}
                  </div>

                  {/* Hover Shimmer */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                      className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-green/5 to-transparent skew-x-12"
                    />
                  </div>

                  <div className="relative">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 8 }}
                      transition={{ duration: 0.4 }}
                      className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-xl text-white mb-5 shadow-md"
                    >
                      <i className={`${item.icon} text-xl sm:text-2xl`} />
                    </motion.div>

                    <div className="text-green text-xs font-bold mb-2 tracking-wider">
                      ADIM {item.number}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy mb-2 group-hover:text-green transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>

                  {/* Bottom Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green via-teal to-green scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Goals */}
      <section
        ref={refGoals}
        id="hedefler"
        className="py-20 lg:py-28 bg-navy relative overflow-hidden"
      >
        {/* Background Elements */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/5 rounded-full"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewGoals ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <span className="text-green text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /ÇEVRESEL HEDEFLER
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 sm:mt-6 mb-4 sm:mb-6">
              Yol Haritamız
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
              Sürdürülebilir gelecek için belirlediğimiz somut hedefler
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {environmentalGoals.map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={inViewGoals ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-green/40 hover:bg-white/10 transition-all duration-500 cursor-default h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.4 }}
                    className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-green/20 to-teal/20 rounded-xl mb-6 group-hover:from-green/30 group-hover:to-teal/30 transition-colors duration-300"
                  >
                    <i className={`${goal.icon} text-green text-2xl`} />
                  </motion.div>

                  <div className="text-5xl sm:text-6xl font-bold text-white mb-4 group-hover:text-green transition-colors duration-300">
                    {goal.year}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-4">{goal.goal}</h3>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inViewGoals ? { width: `${goal.progress}%` } : {}}
                        transition={{ duration: 1.5, delay: 0.5 + index * 0.1, ease: 'easeOut' }}
                        className="h-full bg-gradient-to-r from-green to-teal rounded-full"
                      />
                    </div>
                    <div className="text-white/50 text-xs mt-2">{goal.progress}% Tamamlandı</div>
                  </div>

                  <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm font-medium whitespace-nowrap">
                    {goal.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section ref={refStats} className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewStats ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /ÇEVRESEL ETKİ
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Rakamlarla Sürdürülebilirlik
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Çevre dostu üretim anlayışımızın somut sonuçları
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={inViewStats ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-gradient-to-br from-green/5 to-teal/5 rounded-2xl p-8 text-center border border-green/10 hover:border-green/30 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 8 }}
                    transition={{ duration: 0.4 }}
                    className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl mx-auto mb-6 shadow-lg shadow-green/20"
                  >
                    <i className={`${stat.icon} text-white text-3xl`} />
                  </motion.div>

                  <div className="text-4xl sm:text-5xl font-bold text-navy mb-2 group-hover:text-green transition-colors duration-300">
                    <CountUp end={stat.value} suffix={stat.suffix} inView={inViewStats} />
                  </div>
                  <p className="text-gray-600 text-sm font-medium">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section ref={refCommitment} className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={inViewCommitment ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative w-full h-[450px] lg:h-[550px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/bb89e0e10f9aa7a485c7f462bf574824.jpg"
                  alt="Çevre Taahhüdümüz"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inViewCommitment ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-xl">
                    <i className="ri-earth-line text-white text-2xl" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-navy">2027</div>
                    <div className="text-gray-500 text-sm">Karbon Nötr</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={inViewCommitment ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green/10 rounded-full text-green text-xs font-semibold uppercase tracking-wider mb-6">
                <i className="ri-heart-line" />
                Taahhüdümüz
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 leading-tight">
                Doğaya Saygılı
                <br />
                <span className="bg-gradient-to-r from-green to-teal bg-clip-text text-transparent">
                  Üretim Anlayışı
                </span>
              </h2>
              <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-8">
                Çevre dostu üretim süreçleri ve geri dönüştürülebilir ambalaj çözümleriyle 
                sürdürülebilir bir gelecek inşa ediyoruz.
              </p>

              <div className="space-y-5">
                {commitments.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inViewCommitment ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-green/10 rounded-lg group-hover:bg-green/20 transition-colors duration-300 flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-green text-xl" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-navy mb-1 group-hover:text-green transition-colors duration-300">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={refCTA} className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <motion.div
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 right-20 w-72 h-72 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inViewCTA ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-green via-green to-teal rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div
                className="absolute top-0 left-0 w-full h-full"
                style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px',
                }}
              />
            </div>

            {/* Glowing Orbs */}
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-10 left-10 w-32 h-32 bg-white/20 rounded-full blur-3xl"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute bottom-10 right-10 w-40 h-40 bg-white/20 rounded-full blur-3xl"
            />

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inViewCTA ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-8 backdrop-blur-sm"
              >
                <i className="ri-leaf-line text-white text-3xl" />
              </motion.div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Sürdürülebilir Geleceği
                <br />
                <span className="text-white/90">Birlikte İnşa Edelim</span>
              </h2>
              <p className="text-white/80 text-base lg:text-lg max-w-2xl mx-auto mb-10">
                Çevre dostu ambalaj çözümlerimiz hakkında detaylı bilgi almak ve 
                sürdürülebilirlik yolculuğumuza katılmak için bizimle iletişime geçin.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/contact"
                  className="group relative px-10 py-4 bg-white text-green font-semibold rounded-xl overflow-hidden transition-all duration-500 shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    İletişime Geçin
                    <motion.i
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="ri-arrow-right-line text-lg"
                    />
                  </span>
                </a>
                <a
                  href="/products"
                  className="px-10 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/20 hover:border-white/50 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-box-3-line" />
                    Ürünlerimiz
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SustainabilityPage;
